import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Progress } from '@/components/ui/progress';
import { 
  Tractor, Plus, Search, Camera, Wrench, AlertTriangle, 
  FileText, Package, History, Settings, Upload, Link,
  Clock, Fuel, Gauge, Calendar, DollarSign, Trash2, Edit,
  ExternalLink, Image, BookOpen, Loader2, RefreshCw, Eye,
  ChevronRight, Filter, MoreVertical, Bot, Mic, ClipboardCheck,
  CheckCircle, CheckCircle2, Circle, MessageSquare, Copy
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import api from '@/lib/api';
import { useAutoSave, AutoSaveIndicator } from '@/hooks/useAutoSave';

// Types de machines avec icônes
const TYPES_MACHINES = {
  tracteur: { nom: "Tracteur", icone: "🚜", couleur: "#22c55e" },
  enjambeur: { nom: "Enjambeur", icone: "🚜", couleur: "#06b6d4" },
  vendangeuse: { nom: "Vendangeuse", icone: "🍇", couleur: "#a855f7" },
  moissonneuse: { nom: "Moissonneuse", icone: "🌾", couleur: "#eab308" },
  pulverisateur: { nom: "Pulvérisateur", icone: "💨", couleur: "#3b82f6" },
  semoir: { nom: "Semoir", icone: "🌱", couleur: "#10b981" },
  epandeur: { nom: "Épandeur", icone: "📦", couleur: "#8b5cf6" },
  broyeur: { nom: "Broyeur", icone: "🔪", couleur: "#ef4444" },
  remorque: { nom: "Remorque", icone: "🚛", couleur: "#6b7280" },
  porte_benne: { nom: "Porte-benne", icone: "🛻", couleur: "#0ea5e9" },
  vehicule_routier: { nom: "Véhicule routier", icone: "🚗", couleur: "#f97316" },
  pelle_mecanique: { nom: "Pelle mécanique", icone: "🚧", couleur: "#facc15" },
  autre: { nom: "Autre", icone: "🔧", couleur: "#9ca3af" }
};

// Ordre fixe pour le classement de la liste (groupé par catégorie)
const TYPE_ORDER = [
  'tracteur', 'enjambeur', 'vendangeuse', 'moissonneuse',
  'pulverisateur', 'semoir', 'epandeur', 'broyeur',
  'remorque', 'porte_benne', 'vehicule_routier', 'pelle_mecanique',
  'autre'
];

// Composant Notes & Carnet d'entretien
const NotesSection = ({ machine, onUpdate }) => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newNote, setNewNote] = useState('');
  const [newPhotos, setNewPhotos] = useState([]); // [{url, file}]
  const [uploadingNotePhoto, setUploadingNotePhoto] = useState(false);
  const [savingNote, setSavingNote] = useState(false);
  const [heuresUpdate, setHeuresUpdate] = useState(machine.heures_compteur || 0);
  const [showUpdateCompteur, setShowUpdateCompteur] = useState(false);
  const [savingCompteur, setSavingCompteur] = useState(false);

  useEffect(() => {
    fetchNotes();
  }, [machine.id]);

  const fetchNotes = async () => {
    try {
      const response = await api.get(`/materiel/${machine.id}/notes`);
      setNotes(response.data.notes || []);
    } catch (error) {
      setNotes([]);
    } finally {
      setLoading(false);
    }
  };

  const handleNotePhotoSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Image uniquement'); return; }
    setUploadingNotePhoto(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('is_profile', 'false');
      fd.append('description', `Photo note - ${new Date().toLocaleString('fr-FR')}`);
      const r = await api.post(`/materiel/${machine.id}/upload-photo`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setNewPhotos((prev) => [...prev, r.data.photo]);
      toast.success('Photo ajoutée à la note');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur photo');
    } finally {
      setUploadingNotePhoto(false);
      e.target.value = '';
    }
  };

  const removeNotePhoto = (idx) => setNewPhotos(prev => prev.filter((_, i) => i !== idx));

  const addNote = async () => {
    if (!newNote.trim() && newPhotos.length === 0) return;
    setSavingNote(true);
    try {
      await api.post(`/materiel/${machine.id}/notes`, {
        contenu: newNote || (newPhotos.length ? '(Photo)' : ''),
        heures_compteur: machine.heures_compteur,
        photos: newPhotos.map(p => p.url),
        photo_url: newPhotos[0]?.url || null,
      });
      setNewNote('');
      setNewPhotos([]);
      fetchNotes();
      toast.success('Note ajoutée');
    } catch (error) {
      toast.error('Erreur ajout note');
    } finally { setSavingNote(false); }
  };

  const updateCompteur = async () => {
    setSavingCompteur(true);
    try {
      await api.put(`/materiel/${machine.id}/compteur`, null, {
        params: { heures: heuresUpdate }
      });
      toast.success('Compteur mis à jour');
      setShowUpdateCompteur(false);
      onUpdate?.();
    } catch (error) {
      toast.error('Erreur mise à jour compteur');
    } finally {
      setSavingCompteur(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Section Compteur heures */}
      <Card className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-950/30 dark:to-blue-900/20 border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-blue-600 dark:text-blue-400 font-medium">Compteur horaire</p>
              <p className="text-3xl font-bold text-blue-700 dark:text-blue-300">{machine.heures_compteur || 0} h</p>
              {machine.dernier_entretien && (
                <p className="text-xs text-muted-foreground mt-1">
                  Dernier entretien à {machine.dernier_entretien.heures_compteur || '?'}h
                </p>
              )}
            </div>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setShowUpdateCompteur(!showUpdateCompteur)}
            >
              <Clock className="w-4 h-4 mr-2" />
              Mettre à jour
            </Button>
          </div>
          
          {showUpdateCompteur && (
            <div className="mt-4 pt-4 border-t border-blue-200 flex items-center gap-3">
              <Input
                type="number"
                value={heuresUpdate}
                onChange={(e) => setHeuresUpdate(parseFloat(e.target.value) || 0)}
                className="w-32"
                placeholder="Heures"
              />
              <span className="text-sm text-muted-foreground">heures</span>
              <Button size="sm" onClick={updateCompteur} disabled={savingCompteur}>
                {savingCompteur ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enregistrer'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Ajouter une note */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <h4 className="font-medium flex items-center gap-2">
            <History className="w-4 h-4" />
            Carnet de notes
          </h4>
          <Textarea
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            placeholder="Ajouter une observation, plaque d'immatriculation, problème remarqué..."
            rows={2}
          />

          {/* Photos jointes à la note */}
          {newPhotos.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {newPhotos.map((p, idx) => {
                const fullUrl = p.url?.startsWith('http') ? p.url : `${process.env.REACT_APP_BACKEND_URL}${p.url}`;
                return (
                  <div key={idx} className="relative w-20 h-20 rounded-lg overflow-hidden bg-muted">
                    <img src={fullUrl} alt="" className="w-full h-full object-cover" />
                    <button onClick={() => removeNotePhoto(idx)}
                      className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center"
                      data-testid={`note-photo-remove-${idx}`}>×</button>
                  </div>
                );
              })}
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            <label className="inline-flex items-center px-3 py-1.5 rounded-md border border-input bg-background hover:bg-accent text-sm cursor-pointer"
              data-testid="note-take-photo">
              <Camera className="w-4 h-4 mr-2" />
              {uploadingNotePhoto ? 'Envoi…' : 'Prendre photo'}
              <input type="file" accept="image/*" capture="environment" className="hidden"
                onChange={handleNotePhotoSelect} disabled={uploadingNotePhoto} />
            </label>
            <label className="inline-flex items-center px-3 py-1.5 rounded-md border border-input bg-background hover:bg-accent text-sm cursor-pointer"
              data-testid="note-galerie-photo">
              <Upload className="w-4 h-4 mr-2" />
              Galerie
              <input type="file" accept="image/*" className="hidden"
                onChange={handleNotePhotoSelect} disabled={uploadingNotePhoto} />
            </label>
            <Button onClick={addNote} disabled={savingNote || (!newNote.trim() && newPhotos.length === 0)}
              data-testid="note-save" className="ml-auto">
              {savingNote ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Enregistrer
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Liste des notes */}
      <div className="space-y-2">
        {loading ? (
          <div className="text-center py-8">
            <Loader2 className="w-6 h-6 animate-spin mx-auto" />
          </div>
        ) : notes.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground bg-muted/30 rounded-lg">
            <History className="w-10 h-10 mx-auto mb-2 opacity-30" />
            <p>Aucune note enregistrée</p>
            <p className="text-xs">Ajoutez des observations pour suivre l'état de la machine</p>
          </div>
        ) : (
          notes.map((note, idx) => {
            const photos = note.photos || (note.photo_url ? [note.photo_url] : []);
            return (
              <Card key={note.id || idx} className="bg-muted/30">
                <CardContent className="p-3">
                  <div className="flex justify-between items-start">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm whitespace-pre-wrap">{note.contenu}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {new Date(note.created_at).toLocaleDateString('fr-FR', { 
                          day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
                        })}
                        {note.heures_compteur && ` • ${note.heures_compteur}h`}
                        {note.auteur && ` • ${note.auteur}`}
                      </p>
                      {photos.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          {photos.map((url, pi) => {
                            const full = url?.startsWith('http') ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`;
                            return (
                              <a key={pi} href={full} target="_blank" rel="noreferrer"
                                className="block w-16 h-16 rounded overflow-hidden bg-muted">
                                <img src={full} alt="" className="w-full h-full object-cover" />
                              </a>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Alertes à venir */}
      {machine.alertes && machine.alertes.length > 0 && (
        <Card className="border-amber-200 bg-amber-50/50">
          <CardContent className="p-4">
            <h4 className="font-medium text-amber-700 mb-2 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              Entretiens à prévoir
            </h4>
            <div className="space-y-2">
              {machine.alertes.map((alerte, idx) => (
                <div key={idx} className="flex items-center justify-between p-2 bg-white rounded text-sm">
                  <div className="flex items-center gap-2">
                    <span>{alerte.icone}</span>
                    <span>{alerte.entretien_nom}</span>
                  </div>
                  <Badge variant={alerte.urgence === 'haute' ? 'destructive' : 'secondary'}>
                    {alerte.message}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Composant Checklist d'entretien journalier
const ChecklistEntretienSection = ({ machine }) => {
  const [checklist, setChecklist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastCheck, setLastCheck] = useState(null);

  // Items de checklist par défaut selon le type de machine
  const DEFAULT_CHECKLIST = {
    tracteur: [
      { id: 'niveau_huile', label: 'Niveau d\'huile moteur', icone: '🛢️', categorie: 'Fluides' },
      { id: 'niveau_carburant', label: 'Niveau de carburant', icone: '⛽', categorie: 'Fluides' },
      { id: 'niveau_liquide_refroid', label: 'Liquide de refroidissement', icone: '💧', categorie: 'Fluides' },
      { id: 'niveau_hydraulique', label: 'Niveau huile hydraulique', icone: '🔴', categorie: 'Fluides' },
      { id: 'pression_pneus', label: 'Pression des pneus', icone: '🔵', categorie: 'Pneumatiques' },
      { id: 'etat_pneus', label: 'État des pneus (usure, coupures)', icone: '⚫', categorie: 'Pneumatiques' },
      { id: 'eclairage', label: 'Éclairage et signalisation', icone: '💡', categorie: 'Sécurité' },
      { id: 'freins', label: 'Test des freins', icone: '🛑', categorie: 'Sécurité' },
      { id: 'ceinture', label: 'Ceinture de sécurité', icone: '🪢', categorie: 'Sécurité' },
      { id: 'retroviseurs', label: 'Rétroviseurs propres et réglés', icone: '🪞', categorie: 'Sécurité' },
      { id: 'courroies', label: 'État des courroies', icone: '⚙️', categorie: 'Mécanique' },
      { id: 'fuites', label: 'Absence de fuites', icone: '💦', categorie: 'Mécanique' },
      { id: 'proprete_cabine', label: 'Propreté de la cabine', icone: '🧹', categorie: 'Propreté' },
      { id: 'filtre_air_cabine', label: 'Filtre à air cabine', icone: '🌬️', categorie: 'Filtres' },
    ],
    default: [
      { id: 'etat_general', label: 'État général de la machine', icone: '👁️', categorie: 'Visuel' },
      { id: 'proprete', label: 'Propreté', icone: '🧹', categorie: 'Propreté' },
      { id: 'graissage', label: 'Points de graissage', icone: '🔧', categorie: 'Lubrification' },
      { id: 'fixations', label: 'Vérification des fixations', icone: '🔩', categorie: 'Mécanique' },
      { id: 'usure', label: 'Pièces d\'usure', icone: '⚠️', categorie: 'Mécanique' },
      { id: 'securite', label: 'Dispositifs de sécurité', icone: '🛡️', categorie: 'Sécurité' },
    ]
  };

  useEffect(() => {
    loadChecklist();
  }, [machine.id]);

  const loadChecklist = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/checklist-materiel/machine/${machine.id}/today`);
      if (res.data.checklist) {
        setChecklist(res.data.checklist.items || []);
        setLastCheck(res.data.checklist);
      } else {
        // Charger la checklist par défaut
        const type = machine.type_materiel?.toLowerCase() || 'default';
        const defaultItems = DEFAULT_CHECKLIST[type] || DEFAULT_CHECKLIST.default;
        setChecklist(defaultItems.map(item => ({ ...item, checked: false, note: '' })));
      }
    } catch (error) {
      // Utiliser les items par défaut
      const type = machine.type_materiel?.toLowerCase() || 'default';
      const defaultItems = DEFAULT_CHECKLIST[type] || DEFAULT_CHECKLIST.default;
      setChecklist(defaultItems.map(item => ({ ...item, checked: false, note: '' })));
    } finally {
      setLoading(false);
    }
  };

  const toggleItem = (itemId) => {
    setChecklist(prev => prev.map(item => 
      item.id === itemId ? { ...item, checked: !item.checked } : item
    ));
  };

  const updateNote = (itemId, note) => {
    setChecklist(prev => prev.map(item => 
      item.id === itemId ? { ...item, note } : item
    ));
  };

  const saveChecklist = async () => {
    setSaving(true);
    try {
      await api.post(`/checklist-materiel/machine/${machine.id}/check`, {
        items: checklist,
        machine_id: machine.id,
        machine_nom: machine.nom
      });
      toast.success('Checklist enregistrée');
      setLastCheck({ date: new Date().toISOString() });
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const completedCount = checklist.filter(i => i.checked).length;
  const totalCount = checklist.length;
  const progress = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;

  // Grouper par catégorie
  const categories = [...new Set(checklist.map(i => i.categorie))];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-6">
        <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Barre de progression */}
      <div className="space-y-2">
        <div className="flex justify-between text-sm">
          <span>{completedCount}/{totalCount} vérifications</span>
          <span className={progress === 100 ? 'text-green-600 font-medium' : ''}>
            {Math.round(progress)}%
          </span>
        </div>
        <Progress value={progress} className="h-2" />
        {lastCheck && (
          <p className="text-xs text-muted-foreground">
            Dernière vérification : {new Date(lastCheck.date || lastCheck.created_at).toLocaleString('fr-FR')}
          </p>
        )}
      </div>

      {/* Items par catégorie */}
      <div className="space-y-3 max-h-[400px] overflow-y-auto">
        {categories.map(cat => (
          <div key={cat}>
            <h5 className="text-xs font-semibold text-muted-foreground uppercase mb-2">{cat}</h5>
            <div className="space-y-1">
              {checklist.filter(i => i.categorie === cat).map(item => (
                <div 
                  key={item.id}
                  className={cn(
                    "flex items-center gap-3 p-2 rounded-lg transition-colors cursor-pointer",
                    item.checked ? "bg-green-50 dark:bg-green-950/30" : "hover:bg-muted/50"
                  )}
                  onClick={() => toggleItem(item.id)}
                >
                  {item.checked ? (
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                  ) : (
                    <Circle className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                  )}
                  <span className="text-lg">{item.icone}</span>
                  <span className={cn("flex-1 text-sm", item.checked && "text-green-700")}>{item.label}</span>
                  {item.note && (
                    <Badge variant="outline" className="text-xs">
                      <MessageSquare className="w-3 h-3 mr-1" />
                      Note
                    </Badge>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Boutons action */}
      <div className="flex gap-2 pt-2 border-t">
        <Button 
          onClick={saveChecklist} 
          disabled={saving}
          className="flex-1"
          variant={progress === 100 ? "default" : "outline"}
        >
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ClipboardCheck className="w-4 h-4 mr-2" />}
          {progress === 100 ? 'Valider la checklist' : 'Enregistrer'}
        </Button>
        <Button 
          variant="outline" 
          onClick={() => window.open(`/checklist-materiel?machine=${machine.id}`, '_blank')}
        >
          Historique
        </Button>
      </div>
    </div>
  );
};

// Composant Attelage - Gestion des compatibilités tracteur/outils
const AttelageSection = ({ machine, onUpdate }) => {
  const [config, setConfig] = useState({
    est_tracteur: machine.est_tracteur || false,
    est_attelable: machine.est_attelable || false,
    type_attelage: machine.type_attelage || '',
    poids: machine.poids || '',
    poids_max_attelable: machine.poids_max_attelable || '',
    tracteurs_compatibles: machine.tracteurs_compatibles || [],
    outils_compatibles: machine.outils_compatibles || []
  });
  const [saving, setSaving] = useState(false);
  const [tracteurs, setTracteurs] = useState([]);
  const [attelables, setAttelables] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [tracteursRes, attelablesRes] = await Promise.all([
        api.get('/team/materiels/tracteurs'),
        api.get('/team/materiels/attelables')
      ]);
      setTracteurs(tracteursRes.data.tracteurs || []);
      setAttelables(attelablesRes.data.attelables || []);
    } catch (error) {
      console.error('Erreur chargement données attelage:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/team/materiels/${machine.id}/attelage`, config);
      toast.success('Configuration attelage sauvegardée');
      onUpdate?.();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const toggleTracteurCompatible = (tracteurId) => {
    setConfig(prev => ({
      ...prev,
      tracteurs_compatibles: prev.tracteurs_compatibles.includes(tracteurId)
        ? prev.tracteurs_compatibles.filter(id => id !== tracteurId)
        : [...prev.tracteurs_compatibles, tracteurId]
    }));
  };

  const toggleOutilCompatible = (outilId) => {
    setConfig(prev => ({
      ...prev,
      outils_compatibles: prev.outils_compatibles.includes(outilId)
        ? prev.outils_compatibles.filter(id => id !== outilId)
        : [...prev.outils_compatibles, outilId]
    }));
  };

  const TYPES_ATTELAGE = [
    { value: '3points', label: 'Attelage 3 points' },
    { value: 'timon', label: 'Timon' },
    { value: 'boule', label: 'Boule d\'attelage' },
    { value: 'crochet', label: 'Crochet' },
    { value: 'piton', label: 'Piton' },
    { value: 'chape', label: 'Chape' },
    { value: 'hydraulique', label: 'Coupleur hydraulique' }
  ];

  return (
    <div className="space-y-4">
      {/* Configuration du type */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Link className="w-4 h-4" />
            Type de matériel
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={config.est_tracteur}
                onChange={(e) => setConfig(prev => ({ ...prev, est_tracteur: e.target.checked }))}
                className="w-4 h-4"
              />
              <span className="text-sm font-medium">🚜 C'est un tracteur (peut tracter d'autres matériels)</span>
            </label>
          </div>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={config.est_attelable}
                onChange={(e) => setConfig(prev => ({ ...prev, est_attelable: e.target.checked }))}
                className="w-4 h-4"
              />
              <span className="text-sm font-medium">🔗 Peut être attelé à un tracteur</span>
            </label>
          </div>
        </CardContent>
      </Card>

      {/* Caractéristiques attelage */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Caractéristiques</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-3 gap-4">
          <div>
            <Label className="text-xs">Type d'attelage</Label>
            <Select value={config.type_attelage} onValueChange={(v) => setConfig(prev => ({ ...prev, type_attelage: v }))}>
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Sélectionner..." />
              </SelectTrigger>
              <SelectContent>
                {TYPES_ATTELAGE.map(t => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Poids (kg)</Label>
            <Input
              type="number"
              value={config.poids}
              onChange={(e) => setConfig(prev => ({ ...prev, poids: e.target.value }))}
              placeholder="Ex: 2500"
              className="mt-1"
            />
          </div>
          {config.est_tracteur && (
            <div>
              <Label className="text-xs">Poids max. attelable (kg)</Label>
              <Input
                type="number"
                value={config.poids_max_attelable}
                onChange={(e) => setConfig(prev => ({ ...prev, poids_max_attelable: e.target.value }))}
                placeholder="Ex: 8000"
                className="mt-1"
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Si c'est attelable: liste des tracteurs compatibles */}
      {config.est_attelable && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              🚜 Tracteurs compatibles
              <Badge variant="secondary">{config.tracteurs_compatibles.length} sélectionné(s)</Badge>
            </CardTitle>
            <CardDescription>Sélectionnez les tracteurs qui peuvent tracter ce matériel</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="w-5 h-5 animate-spin" />
              </div>
            ) : tracteurs.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                Aucun tracteur configuré. Marquez d'abord vos tracteurs comme "tracteur" dans leur fiche.
              </p>
            ) : (
              <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                {tracteurs.map(t => (
                  <label
                    key={t.id}
                    className={cn(
                      "flex items-center gap-2 p-2 rounded border cursor-pointer transition-colors",
                      config.tracteurs_compatibles.includes(t.id)
                        ? "bg-green-50 border-green-300 dark:bg-green-950/30"
                        : "hover:bg-muted"
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={config.tracteurs_compatibles.includes(t.id)}
                      onChange={() => toggleTracteurCompatible(t.id)}
                      className="w-4 h-4"
                    />
                    <span className="text-sm">{t.nom}</span>
                    {t.marque && <span className="text-xs text-muted-foreground">({t.marque})</span>}
                  </label>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Si c'est un tracteur: liste des outils compatibles */}
      {config.est_tracteur && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              🔧 Outils compatibles
              <Badge variant="secondary">{config.outils_compatibles.length} sélectionné(s)</Badge>
            </CardTitle>
            <CardDescription>Sélectionnez les matériels que ce tracteur peut tracter</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="w-5 h-5 animate-spin" />
              </div>
            ) : attelables.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                Aucun matériel attelable configuré. Marquez vos outils comme "attelable" dans leur fiche.
              </p>
            ) : (
              <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                {attelables.map(a => (
                  <label
                    key={a.id}
                    className={cn(
                      "flex items-center gap-2 p-2 rounded border cursor-pointer transition-colors",
                      config.outils_compatibles.includes(a.id)
                        ? "bg-blue-50 border-blue-300 dark:bg-blue-950/30"
                        : "hover:bg-muted"
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={config.outils_compatibles.includes(a.id)}
                      onChange={() => toggleOutilCompatible(a.id)}
                      className="w-4 h-4"
                    />
                    <span className="text-sm">{a.nom}</span>
                    {a.type && <span className="text-xs text-muted-foreground">({a.type})</span>}
                  </label>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Bouton sauvegarder */}
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving}>
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
          Sauvegarder la configuration
        </Button>
      </div>
    </div>
  );
};

// Composant carte machine
const MachineCard = ({ machine, onClick }) => {
  const typeInfo = TYPES_MACHINES[machine.type_machine] || TYPES_MACHINES.autre;
  
  return (
    <Card 
      className={cn(
        "cursor-pointer transition-all hover:shadow-lg hover:scale-[1.02]",
        machine.statut === 'en_panne' && "border-red-300 bg-red-50/50",
        machine.statut === 'en_entretien' && "border-amber-300 bg-amber-50/50"
      )}
      onClick={() => onClick(machine)}
      data-testid={`machine-card-${machine.id}`}
    >
      <CardContent className="p-4">
        {/* Image ou placeholder */}
        <div className="relative mb-3 rounded-lg overflow-hidden bg-muted h-32">
          {machine.photo_url ? (
            <img 
              src={machine.photo_url} 
              alt={machine.nom}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-4xl">
              {typeInfo.icone}
            </div>
          )}
          <Badge 
            className={cn(
              "absolute top-2 right-2",
              machine.statut === 'operationnel' && "bg-green-500",
              machine.statut === 'en_panne' && "bg-red-500",
              machine.statut === 'en_entretien' && "bg-amber-500"
            )}
          >
            {machine.statut}
          </Badge>
        </div>
        
        {/* Infos */}
        <div className="space-y-2">
          <h3 className="font-semibold truncate">{machine.nom}</h3>
          <p className="text-sm text-muted-foreground">
            {machine.marque} {machine.modele}
          </p>
          
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-1 text-muted-foreground">
              <Clock className="w-3 h-3" />
              {machine.heures_compteur || 0}h
            </div>
            {machine.annee && (
              <span className="text-muted-foreground">{machine.annee}</span>
            )}
          </div>
          
          {/* Alertes */}
          {machine.nb_alertes > 0 && (
            <div className="flex items-center gap-1 text-red-600 text-sm">
              <AlertTriangle className="w-4 h-4" />
              {machine.nb_alertes} alerte(s) entretien
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// Composant fiche machine détaillée
const FicheMachine = ({ machine, onClose, onUpdate, onDelete }) => {
  const [activeTab, setActiveTab] = useState('general');
  const [entretiens, setEntretiens] = useState([]);
  const [pieces, setPieces] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchingDocs, setSearchingDocs] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Édition machine
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editForm, setEditForm] = useState(machine);
  const [savingEdit, setSavingEdit] = useState(false);
  useEffect(() => { setEditForm(machine); }, [machine]);

  // Auto-save quand on modifie editForm en mode dialog ouvert (debounce 2s)
  const autoSave = useAutoSave({
    data: editForm,
    enabled: showEditDialog && !!editForm?.id,
    delay: 2000,
    deps: [machine?.id, showEditDialog],
    onSave: async (data) => {
      const payload = {
        nom: data.nom,
        marque: data.marque,
        modele: data.modele,
        annee: data.annee ? parseInt(data.annee) : null,
        immatriculation: data.immatriculation,
        numero_serie: data.numero_serie,
        statut: data.statut,
        proprietaire: data.proprietaire,
        notes: data.notes,
        type: data.type,
      };
      await api.put(`/materiel/${data.id}`, payload);
      onUpdate?.();
    },
  });

  // Nouvel entretien
  const [showEntretienDialog, setShowEntretienDialog] = useState(false);
  const [entretienForm, setEntretienForm] = useState({
    type_entretien: 'vidange', description: '',
    date_realisation: new Date().toISOString().slice(0, 10),
    heures_compteur: '', cout: '', notes: '', prestataire: '',
  });
  const [savingEntretien, setSavingEntretien] = useState(false);

  const handleEntretienSave = async () => {
    if (!entretienForm.description?.trim()) { toast.error('Description requise'); return; }
    setSavingEntretien(true);
    try {
      await api.post('/materiel/entretien', {
        machine_id: machine.id,
        type_entretien: entretienForm.type_entretien,
        description: entretienForm.description,
        date_realisation: entretienForm.date_realisation,
        heures_compteur: entretienForm.heures_compteur ? parseFloat(entretienForm.heures_compteur) : null,
        cout: entretienForm.cout ? parseFloat(entretienForm.cout) : null,
        prestataire: entretienForm.prestataire || null,
        notes: entretienForm.notes || null,
      });
      toast.success('Entretien enregistré');
      setShowEntretienDialog(false);
      setEntretienForm({ ...entretienForm, description: '', heures_compteur: '', cout: '', notes: '' });
      // Re-fetch entretiens
      try {
        const r = await api.get(`/materiel/${machine.id}/fiches-entretien`);
        setEntretiens(r.data?.entretiens || []);
      } catch {}
      onUpdate?.();
    } catch (err) {
      console.error('Entretien err:', err);
      toast.error(err.response?.data?.detail || 'Erreur entretien');
    } finally { setSavingEntretien(false); }
  };

  // Nouvelle pièce
  const [showPieceDialog, setShowPieceDialog] = useState(false);
  const [pieceForm, setPieceForm] = useState({ nom: '', reference: '', fournisseur: '', prix_unitaire: '' });
  const [savingPiece, setSavingPiece] = useState(false);

  const handlePieceSave = async () => {
    if (!pieceForm.nom?.trim() || !pieceForm.reference?.trim()) {
      toast.error('Nom et référence requis'); return;
    }
    setSavingPiece(true);
    try {
      await api.post(`/materiel/${machine.id}/pieces`, null, {
        params: {
          nom: pieceForm.nom,
          reference: pieceForm.reference,
          fournisseur: pieceForm.fournisseur || undefined,
          prix_unitaire: pieceForm.prix_unitaire ? parseFloat(pieceForm.prix_unitaire) : undefined,
        }
      });
      toast.success('Pièce ajoutée');
      setShowPieceDialog(false);
      setPieceForm({ nom: '', reference: '', fournisseur: '', prix_unitaire: '' });
      // Re-fetch pieces
      try {
        const r = await api.get(`/materiel/${machine.id}/pieces`);
        setPieces(r.data?.pieces || []);
      } catch {}
    } catch (err) {
      console.error('Piece err:', err);
      toast.error(err.response?.data?.detail || 'Erreur pièce');
    } finally { setSavingPiece(false); }
  };

  const handleEditSave = async () => {
    setSavingEdit(true);
    try {
      const payload = {
        nom: editForm.nom,
        type_machine: editForm.type_machine,
        marque: editForm.marque || '',
        modele: editForm.modele || '',
        annee: editForm.annee || null,
        numero_serie: editForm.numero_serie || '',
        immatriculation: editForm.immatriculation || '',
        puissance_cv: editForm.puissance_cv || null,
        heures_compteur: editForm.heures_compteur || 0,
        km_compteur: editForm.km_compteur || 0,
        statut: editForm.statut || 'operationnel',
        photo_url: editForm.photo_url || '',
        notes: editForm.notes || '',
        // Véhicule routier
        est_routier: !!editForm.est_routier,
        assurance_compagnie: editForm.assurance_compagnie || '',
        assurance_numero_contrat: editForm.assurance_numero_contrat || '',
        assurance_date_echeance: editForm.assurance_date_echeance || null,
        controle_technique_date: editForm.controle_technique_date || null,
        controle_technique_prochaine: editForm.controle_technique_prochaine || null,
      };
      await api.put(`/materiel/${machine.id}`, payload);
      toast.success('Machine modifiée');
      setShowEditDialog(false);
      onUpdate?.({ ...machine, ...payload });
    } catch (err) {
      console.error('Edit err:', err);
      toast.error(err.response?.data?.detail || 'Erreur de modification');
    } finally {
      setSavingEdit(false);
    }
  };

  // Upload document (carte grise, assurance, contrôle technique)
  const [uploadingDoc, setUploadingDoc] = useState(null); // 'carte_grise', 'assurance', etc.
  const handleDocUpload = async (e, doc_type) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingDoc(doc_type);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('doc_type', doc_type);
      const r = await api.post(`/materiel/${machine.id}/upload-document`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const url = r.data.document.url;
      // Update editForm directly
      const fieldUrl = `${doc_type}_url`;
      setEditForm(prev => ({ ...prev, [fieldUrl]: url }));
      toast.success('Document chargé');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur upload document');
    } finally {
      setUploadingDoc(null);
      e.target.value = '';
    }
  };

  // OCR plaque ou carte grise via OpenAI Vision
  const [ocrLoading, setOcrLoading] = useState(null); // 'plaque' | 'cg' | null
  const handleOCR = async (e, type) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Image uniquement'); return; }
    setOcrLoading(type);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('apply', 'true');
      const url = type === 'plaque' ? `/materiel/${machine.id}/ocr-plaque` : `/materiel/${machine.id}/ocr-carte-grise`;
      const r = await api.post(url, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const data = r.data;
      if (type === 'plaque') {
        if (data.immatriculation) {
          setEditForm(prev => ({ ...prev, immatriculation: data.immatriculation, est_routier: true }));
          toast.success(`Plaque détectée : ${data.immatriculation} (confiance ${data.confidence}%)`);
        } else {
          toast.error('Aucune plaque détectée. Réessaie avec une photo plus nette.');
        }
      } else {
        const ex = data.extracted || {};
        if (data.confidence >= 30) {
          setEditForm(prev => ({
            ...prev,
            immatriculation: ex.immatriculation || prev.immatriculation,
            marque: ex.marque || prev.marque,
            modele: ex.modele || prev.modele,
            numero_serie: ex.numero_vin || prev.numero_serie,
            puissance_cv: ex.puissance_cv || prev.puissance_cv,
            annee: (ex.date_mise_en_circulation ? parseInt(ex.date_mise_en_circulation.slice(0,4)) : null) || prev.annee,
            carte_grise_url: data.photo_url,
            est_routier: true,
          }));
          toast.success(`Carte grise lue (confiance ${data.confidence}%) — vérifie les infos`);
        } else {
          toast.error('Carte grise illisible. Refais une photo en bonne lumière.');
        }
      }
    } catch (err) {
      console.error('OCR err:', err);
      toast.error(err.response?.data?.detail || 'Erreur OCR');
    } finally {
      setOcrLoading(null);
      e.target.value = '';
    }
  };
  
  const typeInfo = TYPES_MACHINES[machine.type_machine] || TYPES_MACHINES.autre;
  
  // Charger les données liées
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        // Charger les entretiens
        const entResp = await api.get(`/materiel/${machine.id}`);
        setEntretiens(entResp.data.entretiens || []);
        
        // Charger les pièces liées (stocks de type 'piece' liés à cette machine)
        const piecesResp = await api.get('/stocks', { params: { categorie: 'piece' } });
        setPieces(piecesResp.data.stocks?.filter(s => 
          s.machine_id === machine.id || 
          s.nom?.toLowerCase().includes(machine.marque?.toLowerCase() || '') ||
          s.notes?.includes(machine.id)
        ) || []);
        
      } catch (error) {
        console.error('Erreur chargement données machine:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [machine.id, machine.marque]);
  
  // Recherche IA de documentation
  const rechercherDocumentation = async () => {
    setSearchingDocs(true);
    try {
      const response = await api.post(`/api/materiel/${machine.id}/rechercher-documentation`);
      
      const data = response.data;
      if (data.success) {
        // Mettre à jour les documents trouvés
        const allDocs = [
          ...(data.documentation_trouvee || []),
          ...(data.liens_utiles || []).map(l => ({ 
            titre: l.titre, 
            url_externe: l.url,
            type_document: l.type 
          }))
        ];
        setDocuments(allDocs);
        
        // Afficher les infos trouvées
        if (data.fiche_technique) {
          toast.success(`Fiche technique enrichie ! ${allDocs.length} documents trouvés`);
        } else {
          toast.success(`${allDocs.length} documents trouvés`);
        }
      }
    } catch (error) {
      console.error('Erreur recherche:', error);
      toast.error('Erreur recherche documentation');
    } finally {
      setSearchingDocs(false);
    }
  };

  // Supprimer la machine
  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/materiel/${machine.id}`);
      toast.success('Machine supprimée');
      setShowDeleteConfirm(false);
      onClose();
      onDelete?.();
    } catch (error) {
      console.error('Erreur suppression:', error);
      toast.error('Erreur lors de la suppression');
    } finally {
      setDeleting(false);
    }
  };

  // Upload photo
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [localMachine, setLocalMachine] = useState(machine);
  useEffect(() => { setLocalMachine(machine); }, [machine]);

  const handlePhotoUpload = async (e, isProfile = false) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Sélectionnez une image'); return; }
    if (file.size > 15 * 1024 * 1024) { toast.error('Image > 15 Mo'); return; }
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('is_profile', isProfile ? 'true' : 'false');
      const r = await api.post(`/materiel/${machine.id}/upload-photo`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const data = r.data;
      const updated = {
        ...localMachine,
        photos: [...(localMachine.photos || []), data.photo],
        photo_url: data.photo_url || localMachine.photo_url,
      };
      setLocalMachine(updated);
      toast.success('Photo ajoutée');
      onUpdate?.(updated);
    } catch (err) {
      console.error('Upload photo err:', err);
      toast.error(err.response?.data?.detail || 'Erreur upload photo');
    } finally {
      setUploadingPhoto(false);
      e.target.value = '';
    }
  };
  // Utiliser localMachine pour l'affichage (refresh local après upload)
  machine = localMachine;

  const handlePhotoDelete = async (photoId) => {
    if (!window.confirm('Supprimer cette photo ?')) return;
    try {
      const r = await api.delete(`/materiel/${machine.id}/photos/${photoId}`);
      const newPhotos = (localMachine.photos || []).filter(p => p.id !== photoId);
      const updated = { ...localMachine, photos: newPhotos, photo_url: r.data?.photo_url ?? null };
      setLocalMachine(updated);
      toast.success('Photo supprimée');
      onUpdate?.(updated);
    } catch (err) {
      console.error('Delete photo err:', err);
      toast.error(err.response?.data?.detail || 'Erreur suppression photo');
    }
  };

  const handlePhotoSetProfile = async (photoId) => {
    try {
      const photo = (localMachine.photos || []).find(p => p.id === photoId);
      if (!photo) return;
      const r = await api.post(`/materiel/${machine.id}/photos/${photoId}/set-profile`);
      const newPhotos = (localMachine.photos || []).map(p => ({ ...p, is_profile: p.id === photoId }));
      const updated = { ...localMachine, photos: newPhotos, photo_url: r.data?.photo_url || photo.url };
      setLocalMachine(updated);
      toast.success('Photo principale mise à jour');
      onUpdate?.(updated);
    } catch (err) {
      console.error('Set profile err:', err);
      toast.error(err.response?.data?.detail || 'Erreur changement photo principale');
    }
  };
  
  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <span className="text-3xl">{typeInfo.icone}</span>
            <div>
              <h2 className="text-xl">{machine.nom}</h2>
              <p className="text-sm text-muted-foreground font-normal">
                {machine.marque} {machine.modele} {machine.annee && `(${machine.annee})`}
              </p>
            </div>
            <Badge className={cn(
              "ml-auto",
              machine.statut === 'operationnel' && "bg-green-500",
              machine.statut === 'en_panne' && "bg-red-500",
              machine.statut === 'en_entretien' && "bg-amber-500"
            )}>
              {machine.statut}
            </Badge>
          </DialogTitle>
        </DialogHeader>
        
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 overflow-hidden flex flex-col">
          <TabsList className="flex w-full overflow-x-auto justify-start sm:grid sm:grid-cols-8 h-auto p-1 gap-1 scrollbar-hide">
            <TabsTrigger value="general" className="flex flex-col sm:flex-row items-center gap-1 px-3 py-2 text-xs sm:text-sm whitespace-nowrap min-w-[78px]">
              <Wrench className="w-4 h-4 sm:hidden" />
              <span>Général</span>
            </TabsTrigger>
            <TabsTrigger value="checklist" className="flex flex-col sm:flex-row items-center gap-1 px-3 py-2 text-xs sm:text-sm whitespace-nowrap min-w-[78px]">
              <ClipboardCheck className="w-4 h-4" />
              <span>Checklist</span>
            </TabsTrigger>
            <TabsTrigger value="notes" className="flex flex-col sm:flex-row items-center gap-1 px-3 py-2 text-xs sm:text-sm whitespace-nowrap min-w-[78px]">
              <History className="w-4 h-4 sm:hidden" />
              <span>Notes</span>
            </TabsTrigger>
            <TabsTrigger value="entretien" className="flex flex-col sm:flex-row items-center gap-1 px-3 py-2 text-xs sm:text-sm whitespace-nowrap min-w-[78px]">
              <Wrench className="w-4 h-4 sm:hidden" />
              <span>Entretien</span>
            </TabsTrigger>
            <TabsTrigger value="attelage" className="flex flex-col sm:flex-row items-center gap-1 px-3 py-2 text-xs sm:text-sm whitespace-nowrap min-w-[78px]">
              <Settings className="w-4 h-4 sm:hidden" />
              <span>Attelage</span>
            </TabsTrigger>
            <TabsTrigger value="pieces" className="flex flex-col sm:flex-row items-center gap-1 px-3 py-2 text-xs sm:text-sm whitespace-nowrap min-w-[78px]">
              <Package className="w-4 h-4 sm:hidden" />
              <span>Pièces</span>
            </TabsTrigger>
            <TabsTrigger value="documents" className="flex flex-col sm:flex-row items-center gap-1 px-3 py-2 text-xs sm:text-sm whitespace-nowrap min-w-[78px]">
              <FileText className="w-4 h-4 sm:hidden" />
              <span>Documents</span>
            </TabsTrigger>
            <TabsTrigger value="photos" className="flex flex-col sm:flex-row items-center gap-1 px-3 py-2 text-xs sm:text-sm whitespace-nowrap min-w-[78px]">
              <Camera className="w-4 h-4 sm:hidden" />
              <span>Photos</span>
            </TabsTrigger>
          </TabsList>
          
          <ScrollArea className="flex-1 mt-4">
            {/* Tab Général */}
            <TabsContent value="general" className="space-y-4 m-0">
              <div className="grid grid-cols-2 gap-4">
                {/* Photo principale */}
                <div className="relative rounded-lg overflow-hidden bg-muted h-48 group">
                  {machine.photo_url ? (
                    <>
                      <img 
                        src={machine.photo_url.startsWith('http') ? machine.photo_url : `${process.env.REACT_APP_BACKEND_URL}${machine.photo_url}`} 
                        alt={machine.nom}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute bottom-2 right-2 flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                        <label className="px-2.5 py-1.5 bg-white/90 hover:bg-white text-slate-800 text-xs font-medium rounded-lg cursor-pointer shadow flex items-center gap-1"
                          data-testid="photo-take-replace">
                          <Camera className="w-3 h-3" /> Photo
                          <input type="file" accept="image/*" capture="environment" className="hidden"
                            onChange={(e) => handlePhotoUpload(e, true)} disabled={uploadingPhoto} />
                        </label>
                        <label className="px-2.5 py-1.5 bg-white/90 hover:bg-white text-slate-800 text-xs font-medium rounded-lg cursor-pointer shadow flex items-center gap-1"
                          data-testid="photo-galerie-replace">
                          <Upload className="w-3 h-3" /> Galerie
                          <input type="file" accept="image/*" className="hidden"
                            onChange={(e) => handlePhotoUpload(e, true)} disabled={uploadingPhoto} />
                        </label>
                      </div>
                    </>
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center text-muted-foreground gap-2">
                      <Camera className="w-12 h-12 mb-1" />
                      <span>Aucune photo</span>
                      <div className="flex gap-2">
                        <label className="inline-flex items-center px-3 py-1.5 rounded-md border border-input bg-background hover:bg-accent text-sm font-medium cursor-pointer"
                          data-testid="photo-take">
                          <Camera className="w-4 h-4 mr-2" />
                          {uploadingPhoto ? 'Envoi…' : 'Prendre photo'}
                          <input type="file" accept="image/*" capture="environment" className="hidden"
                            onChange={(e) => handlePhotoUpload(e, true)} disabled={uploadingPhoto} />
                        </label>
                        <label className="inline-flex items-center px-3 py-1.5 rounded-md border border-input bg-background hover:bg-accent text-sm font-medium cursor-pointer"
                          data-testid="photo-galerie">
                          <Upload className="w-4 h-4 mr-2" />
                          Galerie
                          <input type="file" accept="image/*" className="hidden"
                            onChange={(e) => handlePhotoUpload(e, true)} disabled={uploadingPhoto} />
                        </label>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Infos principales */}
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="p-3 bg-muted rounded-lg">
                      <p className="text-muted-foreground">Heures compteur</p>
                      <p className="text-2xl font-bold">{machine.heures_compteur || 0}h</p>
                    </div>
                    <div className="p-3 bg-muted rounded-lg">
                      <p className="text-muted-foreground">Km compteur</p>
                      <p className="text-2xl font-bold">{machine.km_compteur || 0} km</p>
                    </div>
                    <div className="p-3 bg-muted rounded-lg">
                      <p className="text-muted-foreground">Puissance</p>
                      <p className="text-2xl font-bold">{machine.puissance_cv || '-'} cv</p>
                    </div>
                    <div className="p-3 bg-muted rounded-lg">
                      <p className="text-muted-foreground">Année</p>
                      <p className="text-2xl font-bold">{machine.annee || '-'}</p>
                    </div>
                  </div>
                  
                  {machine.immatriculation && (
                    <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                      <p className="text-sm text-muted-foreground">Immatriculation</p>
                      <p className="font-mono font-bold">{machine.immatriculation}</p>
                    </div>
                  )}
                  
                  {machine.numero_serie && (
                    <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                      <p className="text-sm text-muted-foreground">N° de série</p>
                      <p className="font-mono text-sm">{machine.numero_serie}</p>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Infos complémentaires */}
              <div className="grid grid-cols-3 gap-4">
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 text-muted-foreground mb-1">
                      <DollarSign className="w-4 h-4" />
                      <span className="text-sm">Prix d'achat</span>
                    </div>
                    <p className="text-xl font-bold">
                      {machine.prix_achat ? `${machine.prix_achat.toLocaleString()} €` : '-'}
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 text-muted-foreground mb-1">
                      <Calendar className="w-4 h-4" />
                      <span className="text-sm">Date d'achat</span>
                    </div>
                    <p className="text-xl font-bold">
                      {machine.date_achat ? new Date(machine.date_achat).toLocaleDateString('fr-FR') : '-'}
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 text-muted-foreground mb-1">
                      <Gauge className="w-4 h-4" />
                      <span className="text-sm">Valeur actuelle</span>
                    </div>
                    <p className="text-xl font-bold">
                      {machine.valeur_actuelle ? `${machine.valeur_actuelle.toLocaleString()} €` : '-'}
                    </p>
                  </CardContent>
                </Card>
              </div>
              
              {machine.notes && (
                <Card>
                  <CardContent className="p-4">
                    <h4 className="font-medium mb-2">Notes</h4>
                    <p className="text-sm text-muted-foreground">{machine.notes}</p>
                  </CardContent>
                </Card>
              )}
            </TabsContent>
            
            {/* Tab Checklist - Checklist complète de la machine */}
            <TabsContent value="checklist" className="space-y-4 m-0">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ClipboardCheck className="w-5 h-5 text-green-600" />
                    Checklist {machine.nom}
                  </CardTitle>
                  <CardDescription>
                    Vérifications et contrôles spécifiques à ce {typeInfo.nom.toLowerCase()}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Résumé du dernier contrôle */}
                  <div className="p-4 bg-muted rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-medium">Dernier contrôle</span>
                      <Badge variant="outline" className="text-green-600">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Conforme
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Il y a 2 jours • Par Laurent S.
                    </p>
                  </div>
                  
                  {/* Aperçu checklist rapide */}
                  <ChecklistEntretienSection machine={machine} />
                  
                  {/* Bouton vers checklist complète */}
                  <div className="pt-4 border-t">
                    <Button 
                      className="w-full gap-2"
                      onClick={() => window.location.href = `/checklist-materiel?machine=${machine.id}`}
                    >
                      <ClipboardCheck className="w-4 h-4" />
                      Ouvrir la checklist complète
                      <ChevronRight className="w-4 h-4 ml-auto" />
                    </Button>
                    <p className="text-xs text-muted-foreground text-center mt-2">
                      Accédez à toutes les vérifications : sécurité, mécanique, électrique, hydraulique...
                    </p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            
            {/* Tab Notes - Carnet de notes et suivi d'utilisation */}
            <TabsContent value="notes" className="space-y-4 m-0">
              <NotesSection machine={machine} onUpdate={onUpdate} />
            </TabsContent>
            
            {/* Tab Entretien */}
            <TabsContent value="entretien" className="space-y-4 m-0">
              {/* Checklist d'entretien journalier */}
              <Card className="border-blue-200 bg-blue-50/30">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <ClipboardCheck className="w-4 h-4 text-blue-600" />
                    Checklist d'entretien journalier
                  </CardTitle>
                  <CardDescription>Vérifications quotidiennes avant utilisation</CardDescription>
                </CardHeader>
                <CardContent>
                  <ChecklistEntretienSection machine={machine} />
                </CardContent>
              </Card>

              <div className="flex justify-between items-center">
                <h3 className="font-semibold">Historique des entretiens</h3>
                <div className="flex gap-2">
                  <Button 
                    size="sm" 
                    variant="outline"
                    className="gap-2 text-green-700 border-green-300 hover:bg-green-50"
                    onClick={() => window.open(`/bot-interventions?mode=entretien&machine=${machine.id}`, '_blank')}
                    data-testid="btn-assistant-entretien"
                  >
                    <Mic className="w-4 h-4" />
                    Assistant vocal
                  </Button>
                  <Button size="sm" onClick={() => setShowEntretienDialog(true)}
                    data-testid="btn-new-entretien">
                    <Plus className="w-4 h-4 mr-2" />
                    Nouvel entretien
                  </Button>
                </div>
              </div>
              
              {/* Alertes entretien */}
              {machine.alertes && machine.alertes.length > 0 && (
                <Card className="border-red-200 bg-red-50/50">
                  <CardContent className="p-4">
                    <h4 className="font-medium text-red-700 mb-2 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      Entretiens à prévoir
                    </h4>
                    <div className="space-y-2">
                      {machine.alertes.map((alerte, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2 bg-white rounded">
                          <div className="flex items-center gap-2">
                            <span>{alerte.icone}</span>
                            <span className="font-medium">{alerte.entretien_nom}</span>
                          </div>
                          <Badge variant={alerte.urgence === 'haute' ? 'destructive' : 'secondary'}>
                            {alerte.message}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
              
              {/* Liste entretiens */}
              <div className="space-y-2">
                {entretiens.map((ent, idx) => (
                  <Card key={idx}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <span className="text-2xl">{ent.type_icone || '🔧'}</span>
                          <div>
                            <h4 className="font-medium">{ent.type_nom || ent.type_entretien}</h4>
                            <p className="text-sm text-muted-foreground">{ent.description}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-medium">{new Date(ent.date_realisation).toLocaleDateString('fr-FR')}</p>
                          {ent.cout && <p className="text-sm text-green-600">{ent.cout} €</p>}
                          {ent.heures_compteur && (
                            <p className="text-xs text-muted-foreground">{ent.heures_compteur}h</p>
                          )}
                        </div>
                      </div>
                      
                      {ent.pieces_utilisees && ent.pieces_utilisees.length > 0 && (
                        <div className="mt-3 pt-3 border-t">
                          <p className="text-xs text-muted-foreground mb-1">Pièces utilisées:</p>
                          <div className="flex flex-wrap gap-1">
                            {ent.pieces_utilisees.map((p, i) => (
                              <Badge key={i} variant="outline" className="text-xs">
                                {p.nom} x{p.quantite}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
                
                {entretiens.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    <Wrench className="w-12 h-12 mx-auto mb-2 opacity-30" />
                    <p>Aucun entretien enregistré</p>
                  </div>
                )}
              </div>
            </TabsContent>
            
            {/* Tab Attelage - Compatibilités tracteur/outils */}
            <TabsContent value="attelage" className="space-y-4 m-0">
              <AttelageSection machine={machine} onUpdate={onUpdate} />
            </TabsContent>
            
            {/* Tab Pièces */}
            <TabsContent value="pieces" className="space-y-4 m-0">
              <div className="flex justify-between items-center">
                <h3 className="font-semibold">Pièces & Consommables liés</h3>
                <Button size="sm" onClick={() => setShowPieceDialog(true)}
                  data-testid="btn-link-piece">
                  <Plus className="w-4 h-4 mr-2" />
                  Lier une pièce
                </Button>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                {pieces.map((piece, idx) => (
                  <Card key={idx} className={cn(
                    piece.en_alerte && "border-red-300 bg-red-50/50"
                  )}>
                    <CardContent className="p-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium text-sm">{piece.nom}</h4>
                          <p className="text-xs text-muted-foreground">{piece.sous_categorie}</p>
                        </div>
                        <div className="text-right">
                          <p className={cn(
                            "font-bold",
                            piece.en_alerte && "text-red-600"
                          )}>
                            {piece.quantite} {piece.unite}
                          </p>
                          {piece.en_alerte && (
                            <Badge variant="destructive" className="text-xs">Stock bas</Badge>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
              
              {pieces.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  <Package className="w-12 h-12 mx-auto mb-2 opacity-30" />
                  <p>Aucune pièce liée</p>
                  <p className="text-xs mt-1">Ajoutez des pièces du stock et liez-les à cette machine</p>
                </div>
              )}
            </TabsContent>
            
            {/* Tab Documents */}
            <TabsContent value="documents" className="space-y-4 m-0">
              <div className="flex justify-between items-center">
                <h3 className="font-semibold">Documentation technique</h3>
                <div className="flex gap-2">
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={() => window.open(`/assistants-ia?agent=mecanicien&machine=${machine.id}&marque=${encodeURIComponent(machine.marque || '')}&modele=${encodeURIComponent(machine.modele || '')}`, '_blank')}
                    className="gap-2 text-orange-700 border-orange-300 hover:bg-orange-50"
                  >
                    <Bot className="w-4 h-4" />
                    Bot Mécanicien
                  </Button>
                  <Button size="sm" variant="outline" onClick={rechercherDocumentation} disabled={searchingDocs}>
                    {searchingDocs ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Search className="w-4 h-4 mr-2" />
                    )}
                    Recherche auto
                  </Button>
                  <Button size="sm">
                    <Upload className="w-4 h-4 mr-2" />
                    Uploader
                  </Button>
                </div>
              </div>
              
              {/* Documents existants */}
              {machine.documents && machine.documents.length > 0 && (
                <div className="space-y-2">
                  {machine.documents.map((doc, idx) => (
                    <Card key={idx}>
                      <CardContent className="p-3 flex items-center gap-3">
                        <FileText className="w-8 h-8 text-blue-500" />
                        <div className="flex-1">
                          <p className="font-medium">{doc.nom || `Document ${idx + 1}`}</p>
                          <p className="text-xs text-muted-foreground">{doc.type}</p>
                        </div>
                        <Button size="sm" variant="ghost">
                          <ExternalLink className="w-4 h-4" />
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
              
              {/* Résultats recherche IA */}
              {documents.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-muted-foreground">Trouvés par l'IA</h4>
                  {documents.map((doc, idx) => (
                    <Card key={idx} className="border-dashed">
                      <CardContent className="p-3 flex items-center gap-3">
                        <BookOpen className="w-8 h-8 text-purple-500" />
                        <div className="flex-1">
                          <p className="font-medium">{doc.titre}</p>
                          <p className="text-xs text-muted-foreground">{doc.type} - {doc.source}</p>
                        </div>
                        <Button size="sm" variant="outline" asChild>
                          <a href={doc.url} target="_blank" rel="noreferrer">
                            <ExternalLink className="w-4 h-4 mr-2" />
                            Voir
                          </a>
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
              
              {(!machine.documents || machine.documents.length === 0) && documents.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  <BookOpen className="w-12 h-12 mx-auto mb-2 opacity-30" />
                  <p>Aucun document</p>
                  <p className="text-xs mt-1">
                    Cliquez sur "Rechercher avec IA" pour trouver automatiquement<br />
                    les revues techniques, vues éclatées, manuels...
                  </p>
                </div>
              )}
            </TabsContent>
            
            {/* Tab Photos */}
            <TabsContent value="photos" className="space-y-4 m-0">
              <div className="flex justify-between items-center">
                <h3 className="font-semibold">Galerie photos ({(machine.photos || []).length})</h3>
                <div className="flex gap-2">
                  <label className="inline-flex items-center px-3 py-1.5 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 text-sm font-medium cursor-pointer"
                    data-testid="gallery-take-photo">
                    <Camera className="w-4 h-4 mr-2" />
                    Photo
                    <input type="file" accept="image/*" capture="environment" className="hidden"
                      onChange={(e) => handlePhotoUpload(e, false)} disabled={uploadingPhoto} />
                  </label>
                  <label className="inline-flex items-center px-3 py-1.5 rounded-md border border-input bg-background hover:bg-accent text-sm font-medium cursor-pointer"
                    data-testid="gallery-upload-photo">
                    <Upload className="w-4 h-4 mr-2" />
                    Galerie
                    <input type="file" accept="image/*" className="hidden"
                      onChange={(e) => handlePhotoUpload(e, false)} disabled={uploadingPhoto} />
                  </label>
                </div>
              </div>
              
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {(machine.photos || []).map((photo, idx) => {
                  const url = typeof photo === 'string' ? photo : photo.url;
                  const fullUrl = url?.startsWith('http') ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`;
                  const photoId = typeof photo === 'object' ? photo.id : null;
                  const isProfile = !!photo.is_profile;
                  return (
                    <div key={photoId || idx}
                      className={`relative aspect-square rounded-lg overflow-hidden bg-muted group transition-all ${isProfile ? 'ring-4 ring-emerald-500 ring-offset-2' : ''}`}
                      data-testid={`gallery-photo-${idx}`}>
                      <a href={fullUrl} target="_blank" rel="noreferrer" className="block w-full h-full">
                        <img src={fullUrl} alt={photo.description || `Photo ${idx + 1}`} className="w-full h-full object-cover" />
                      </a>

                      {/* Badge "Profil" en haut */}
                      {isProfile && (
                        <span className="absolute top-1.5 left-1.5 text-[10px] px-2 py-0.5 bg-emerald-600 text-white rounded-full font-bold flex items-center gap-1 shadow-lg">
                          <CheckCircle2 className="w-3 h-3" /> PHOTO DE PROFIL
                        </span>
                      )}

                      {/* Boutons d'action (toujours visibles sur mobile, hover sur desktop) */}
                      {photoId && (
                        <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/90 to-transparent pt-8 pb-2 px-2 flex items-center justify-between gap-1">
                          {!isProfile ? (
                            <button onClick={(e) => { e.preventDefault(); handlePhotoSetProfile(photoId); }}
                              className="flex-1 px-2 py-1.5 rounded bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] font-semibold flex items-center justify-center gap-1 shadow"
                              data-testid={`photo-set-profile-${idx}`}>
                              <CheckCircle2 className="w-3.5 h-3.5" /> Définir profil
                            </button>
                          ) : (
                            <span className="flex-1 text-center text-[11px] text-emerald-300 font-semibold py-1.5">★ Profil actif</span>
                          )}
                          <button onClick={(e) => { e.preventDefault(); handlePhotoDelete(photoId); }}
                            className="w-8 h-8 rounded bg-red-500 hover:bg-red-600 text-white flex items-center justify-center shadow shrink-0"
                            title="Supprimer cette photo"
                            data-testid={`photo-delete-${idx}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}

                {/* Placeholder pour ajouter */}
                <label className="aspect-square rounded-lg border-2 border-dashed flex flex-col items-center justify-center text-muted-foreground hover:bg-muted/50 cursor-pointer"
                  data-testid="gallery-add-placeholder">
                  <Camera className="w-8 h-8 mb-2" />
                  <span className="text-sm">{uploadingPhoto ? 'Envoi…' : 'Ajouter'}</span>
                  <input type="file" accept="image/*" capture="environment" className="hidden"
                    onChange={(e) => handlePhotoUpload(e, false)}
                    disabled={uploadingPhoto} />
                </label>
              </div>
            </TabsContent>
          </ScrollArea>
        </Tabs>
        
        <DialogFooter className="mt-4 flex justify-between">
          <Button 
            variant="destructive" 
            onClick={() => setShowDeleteConfirm(true)}
            data-testid="btn-delete-machine"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Supprimer
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Fermer</Button>
            <Button onClick={() => { setEditForm(machine); setShowEditDialog(true); }}
              data-testid="btn-edit-machine">
              <Edit className="w-4 h-4 mr-2" />
              Modifier
            </Button>
          </div>
        </DialogFooter>

        {/* Dialog de confirmation de suppression */}
        <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="w-5 h-5" />
                Confirmer la suppression
              </DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <p className="text-sm text-muted-foreground">
                Êtes-vous sûr de vouloir supprimer <strong>{machine.nom}</strong> ?
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                Cette action est irréversible. Toutes les données associées (entretiens, notes, etc.) seront également supprimées.
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>
                Annuler
              </Button>
              <Button 
                variant="destructive" 
                onClick={handleDelete}
                disabled={deleting}
                data-testid="btn-confirm-delete-machine"
              >
                {deleting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4 mr-2" />
                )}
                Supprimer définitivement
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog Modifier la machine */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="edit-machine-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center justify-between gap-2">
                <span className="flex items-center gap-2">
                  <Edit className="w-5 h-5" /> Modifier {machine.nom}
                </span>
                <AutoSaveIndicator status={autoSave.status} lastSaved={autoSave.lastSaved} error={autoSave.error} />
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nom *</Label>
                  <Input value={editForm.nom || ''} onChange={(e) => setEditForm({...editForm, nom: e.target.value})}
                    data-testid="edit-machine-nom" />
                </div>
                <div className="space-y-2">
                  <Label>Type</Label>
                  <Select value={editForm.type_machine || 'autre'}
                    onValueChange={(v) => setEditForm({...editForm, type_machine: v})}>
                    <SelectTrigger data-testid="edit-machine-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(TYPES_MACHINES).map(([key, info]) => (
                        <SelectItem key={key} value={key}>{info.icone} {info.nom}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Marque</Label>
                  <Input value={editForm.marque || ''} onChange={(e) => setEditForm({...editForm, marque: e.target.value})} />
                </div>
                <div className="space-y-2">
                  <Label>Modèle</Label>
                  <Input value={editForm.modele || ''} onChange={(e) => setEditForm({...editForm, modele: e.target.value})} />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Année</Label>
                  <Input type="number" value={editForm.annee || ''} onChange={(e) => setEditForm({...editForm, annee: parseInt(e.target.value) || null})} />
                </div>
                <div className="space-y-2">
                  <Label>Puissance (cv)</Label>
                  <Input type="number" value={editForm.puissance_cv || ''} onChange={(e) => setEditForm({...editForm, puissance_cv: parseInt(e.target.value) || null})} />
                </div>
                <div className="space-y-2">
                  <Label>Heures compteur</Label>
                  <Input type="number" value={editForm.heures_compteur || 0}
                    onChange={(e) => setEditForm({...editForm, heures_compteur: parseFloat(e.target.value) || 0})}
                    data-testid="edit-machine-heures" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Immatriculation</Label>
                  <div className="flex gap-1.5">
                    <Input value={editForm.immatriculation || ''} onChange={(e) => setEditForm({...editForm, immatriculation: e.target.value})}
                      placeholder="AB-123-CD" className="flex-1" />
                    <label className="inline-flex items-center px-2.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs cursor-pointer whitespace-nowrap"
                      title="Photo de la plaque pour reconnaissance auto"
                      data-testid="ocr-plaque-btn">
                      {ocrLoading === 'plaque' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
                      <input type="file" accept="image/*" capture="environment" className="hidden"
                        onChange={(e) => handleOCR(e, 'plaque')} disabled={ocrLoading} />
                    </label>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>N° de série</Label>
                  <Input value={editForm.numero_serie || ''} onChange={(e) => setEditForm({...editForm, numero_serie: e.target.value})} />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>État</Label>
                  <Select value={editForm.statut || 'operationnel'}
                    onValueChange={(v) => setEditForm({...editForm, statut: v})}>
                    <SelectTrigger data-testid="edit-machine-statut"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="operationnel">✅ Opérationnel</SelectItem>
                      <SelectItem value="en_panne">🔴 En panne</SelectItem>
                      <SelectItem value="en_entretien">🔧 En entretien</SelectItem>
                      <SelectItem value="reforme">📦 Réformé</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Km compteur</Label>
                  <Input type="number" value={editForm.km_compteur || 0}
                    onChange={(e) => setEditForm({...editForm, km_compteur: parseFloat(e.target.value) || 0})} />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Notes / Commentaires</Label>
                <Textarea rows={3} value={editForm.notes || ''}
                  onChange={(e) => setEditForm({...editForm, notes: e.target.value})}
                  placeholder="Observations, particularités, état général..." />
              </div>

              {/* Véhicule routier */}
              <div className="border-t pt-4 space-y-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={!!editForm.est_routier}
                    onChange={(e) => setEditForm({...editForm, est_routier: e.target.checked})}
                    className="w-4 h-4" data-testid="edit-est-routier" />
                  <span className="font-semibold">🚗 Véhicule routier (carte grise + assurance + CT)</span>
                </label>

                {editForm.est_routier && (
                  <div className="space-y-3 pl-6">
                    {/* Action rapide IA : scanner carte grise pour pré-remplir tout */}
                    <div className="bg-gradient-to-r from-emerald-50 to-blue-50 dark:from-emerald-950/30 dark:to-blue-950/30 border border-emerald-300/50 rounded-lg p-3">
                      <div className="flex items-center justify-between gap-3">
                        <div className="text-xs text-emerald-900 dark:text-emerald-200">
                          <strong>📷 Scanner carte grise</strong>
                          <p className="text-[11px] opacity-80 mt-0.5">L'IA extrait immat, marque, modèle, n° série, puissance et année automatiquement.</p>
                        </div>
                        <label className="inline-flex items-center px-3 py-2 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium cursor-pointer whitespace-nowrap"
                          data-testid="ocr-carte-grise-btn">
                          {ocrLoading === 'cg'
                            ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Lecture…</>
                            : <><Camera className="w-4 h-4 mr-1.5" /> Scanner</>}
                          <input type="file" accept="image/*" capture="environment" className="hidden"
                            onChange={(e) => handleOCR(e, 'cg')} disabled={ocrLoading} />
                        </label>
                      </div>
                    </div>

                    {/* Carte grise */}
                    <div className="bg-slate-50 dark:bg-slate-900/50 rounded-lg p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">📄 Carte grise</span>
                        <div className="flex items-center gap-1.5">
                          {editForm.carte_grise_url && (
                            <a href={`${process.env.REACT_APP_BACKEND_URL}${editForm.carte_grise_url}`}
                              target="_blank" rel="noreferrer"
                              className="text-xs text-emerald-700 underline">Voir</a>
                          )}
                          <label className="inline-flex items-center px-2 py-1 rounded bg-blue-600 text-white text-xs cursor-pointer hover:bg-blue-700"
                            data-testid="upload-carte-grise">
                            <Upload className="w-3 h-3 mr-1" />
                            {uploadingDoc === 'carte_grise' ? 'Envoi…' : (editForm.carte_grise_url ? 'Remplacer' : 'Charger')}
                            <input type="file" accept="image/*,application/pdf" className="hidden"
                              onChange={(e) => handleDocUpload(e, 'carte_grise')}
                              disabled={uploadingDoc === 'carte_grise'} />
                          </label>
                          <label className="inline-flex items-center px-2 py-1 rounded border border-input bg-background text-xs cursor-pointer hover:bg-accent">
                            <Camera className="w-3 h-3 mr-1" /> Photo
                            <input type="file" accept="image/*" capture="environment" className="hidden"
                              onChange={(e) => handleDocUpload(e, 'carte_grise')}
                              disabled={uploadingDoc === 'carte_grise'} />
                          </label>
                        </div>
                      </div>
                    </div>

                    {/* Assurance */}
                    <div className="bg-slate-50 dark:bg-slate-900/50 rounded-lg p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">🛡️ Assurance</span>
                        <div className="flex items-center gap-1.5">
                          {editForm.assurance_url && (
                            <a href={`${process.env.REACT_APP_BACKEND_URL}${editForm.assurance_url}`}
                              target="_blank" rel="noreferrer"
                              className="text-xs text-emerald-700 underline">Voir</a>
                          )}
                          <label className="inline-flex items-center px-2 py-1 rounded bg-blue-600 text-white text-xs cursor-pointer hover:bg-blue-700"
                            data-testid="upload-assurance">
                            <Upload className="w-3 h-3 mr-1" />
                            {uploadingDoc === 'assurance' ? 'Envoi…' : (editForm.assurance_url ? 'Remplacer' : 'Charger')}
                            <input type="file" accept="image/*,application/pdf" className="hidden"
                              onChange={(e) => handleDocUpload(e, 'assurance')}
                              disabled={uploadingDoc === 'assurance'} />
                          </label>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <Input placeholder="Compagnie" value={editForm.assurance_compagnie || ''}
                          onChange={(e) => setEditForm({...editForm, assurance_compagnie: e.target.value})}
                          className="text-sm" />
                        <Input placeholder="N° contrat" value={editForm.assurance_numero_contrat || ''}
                          onChange={(e) => setEditForm({...editForm, assurance_numero_contrat: e.target.value})}
                          className="text-sm" />
                      </div>
                      <div>
                        <Label className="text-xs">Échéance</Label>
                        <Input type="date" value={editForm.assurance_date_echeance || ''}
                          onChange={(e) => setEditForm({...editForm, assurance_date_echeance: e.target.value})}
                          className="text-sm" />
                      </div>
                    </div>

                    {/* Contrôle technique */}
                    <div className="bg-slate-50 dark:bg-slate-900/50 rounded-lg p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">🔧 Contrôle technique</span>
                        <div className="flex items-center gap-1.5">
                          {editForm.controle_technique_url && (
                            <a href={`${process.env.REACT_APP_BACKEND_URL}${editForm.controle_technique_url}`}
                              target="_blank" rel="noreferrer"
                              className="text-xs text-emerald-700 underline">Voir</a>
                          )}
                          <label className="inline-flex items-center px-2 py-1 rounded bg-blue-600 text-white text-xs cursor-pointer hover:bg-blue-700"
                            data-testid="upload-controle-technique">
                            <Upload className="w-3 h-3 mr-1" />
                            {uploadingDoc === 'controle_technique' ? 'Envoi…' : (editForm.controle_technique_url ? 'Remplacer' : 'Charger')}
                            <input type="file" accept="image/*,application/pdf" className="hidden"
                              onChange={(e) => handleDocUpload(e, 'controle_technique')}
                              disabled={uploadingDoc === 'controle_technique'} />
                          </label>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-xs">Dernier CT</Label>
                          <Input type="date" value={editForm.controle_technique_date || ''}
                            onChange={(e) => setEditForm({...editForm, controle_technique_date: e.target.value})}
                            className="text-sm" />
                        </div>
                        <div>
                          <Label className="text-xs">Prochain CT</Label>
                          <Input type="date" value={editForm.controle_technique_prochaine || ''}
                            onChange={(e) => setEditForm({...editForm, controle_technique_prochaine: e.target.value})}
                            className="text-sm" />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditDialog(false)}>Annuler</Button>
              <Button onClick={handleEditSave} disabled={savingEdit || !editForm.nom?.trim()}
                data-testid="edit-machine-save">
                {savingEdit ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Edit className="w-4 h-4 mr-2" />}
                Enregistrer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog Nouvel entretien */}
        <Dialog open={showEntretienDialog} onOpenChange={setShowEntretienDialog}>
          <DialogContent className="max-w-lg" data-testid="entretien-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Wrench className="w-5 h-5" /> Nouvel entretien</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Type</Label>
                  <Select value={entretienForm.type_entretien}
                    onValueChange={(v) => setEntretienForm({...entretienForm, type_entretien: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="vidange">🛢️ Vidange</SelectItem>
                      <SelectItem value="filtre">🔄 Filtres</SelectItem>
                      <SelectItem value="courroie">⛓️ Courroies</SelectItem>
                      <SelectItem value="pneumatique">🛞 Pneumatiques</SelectItem>
                      <SelectItem value="revision">🔧 Révision</SelectItem>
                      <SelectItem value="reparation">🛠️ Réparation</SelectItem>
                      <SelectItem value="autre">📋 Autre</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>Date</Label>
                  <Input type="date" value={entretienForm.date_realisation}
                    onChange={(e) => setEntretienForm({...entretienForm, date_realisation: e.target.value})} />
                </div>
              </div>
              <div className="space-y-1">
                <Label>Description *</Label>
                <Textarea rows={2} value={entretienForm.description}
                  onChange={(e) => setEntretienForm({...entretienForm, description: e.target.value})}
                  placeholder="Ex: Vidange moteur 10W40, filtres à huile et air remplacés"
                  data-testid="entretien-desc" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label>Heures</Label>
                  <Input type="number" value={entretienForm.heures_compteur}
                    onChange={(e) => setEntretienForm({...entretienForm, heures_compteur: e.target.value})}
                    placeholder={machine.heures_compteur || '0'} />
                </div>
                <div className="space-y-1">
                  <Label>Coût (€)</Label>
                  <Input type="number" step="0.01" value={entretienForm.cout}
                    onChange={(e) => setEntretienForm({...entretienForm, cout: e.target.value})} />
                </div>
                <div className="space-y-1">
                  <Label>Prestataire</Label>
                  <Input value={entretienForm.prestataire}
                    onChange={(e) => setEntretienForm({...entretienForm, prestataire: e.target.value})}
                    placeholder="Garage ou interne" />
                </div>
              </div>
              <div className="space-y-1">
                <Label>Notes</Label>
                <Input value={entretienForm.notes}
                  onChange={(e) => setEntretienForm({...entretienForm, notes: e.target.value})} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEntretienDialog(false)}>Annuler</Button>
              <Button onClick={handleEntretienSave} disabled={savingEntretien || !entretienForm.description?.trim()}
                data-testid="entretien-save">
                {savingEntretien ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                Enregistrer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog Lier une pièce */}
        <Dialog open={showPieceDialog} onOpenChange={setShowPieceDialog}>
          <DialogContent className="max-w-md" data-testid="piece-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Package className="w-5 h-5" /> Lier une pièce</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div className="space-y-1">
                <Label>Nom de la pièce *</Label>
                <Input value={pieceForm.nom} onChange={(e) => setPieceForm({...pieceForm, nom: e.target.value})}
                  placeholder="Ex: Filtre à huile" data-testid="piece-nom" />
              </div>
              <div className="space-y-1">
                <Label>Référence *</Label>
                <Input value={pieceForm.reference} onChange={(e) => setPieceForm({...pieceForm, reference: e.target.value})}
                  placeholder="Ex: HU 925/4 X" data-testid="piece-ref" />
              </div>
              <div className="space-y-1">
                <Label>Fournisseur</Label>
                <Input value={pieceForm.fournisseur} onChange={(e) => setPieceForm({...pieceForm, fournisseur: e.target.value})}
                  placeholder="Ex: Mann-Filter" />
              </div>
              <div className="space-y-1">
                <Label>Prix unitaire (€)</Label>
                <Input type="number" step="0.01" value={pieceForm.prix_unitaire}
                  onChange={(e) => setPieceForm({...pieceForm, prix_unitaire: e.target.value})} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowPieceDialog(false)}>Annuler</Button>
              <Button onClick={handlePieceSave} disabled={savingPiece || !pieceForm.nom?.trim() || !pieceForm.reference?.trim()}
                data-testid="piece-save">
                {savingPiece ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                Lier
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
};

// Page principale Matériel
export default function MaterielPage() {
  const [machines, setMachines] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedMachine, setSelectedMachine] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statutFilter, setStatutFilter] = useState('all');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showDoublonsDialog, setShowDoublonsDialog] = useState(false);
  const [doublonsGroupes, setDoublonsGroupes] = useState([]);
  const [loadingDoublons, setLoadingDoublons] = useState(false);
  const [keepIds, setKeepIds] = useState({}); // {nom_normalise: keep_id}
  
  // Form nouvelle machine
  const [newMachine, setNewMachine] = useState({
    nom: '',
    type_machine: 'tracteur',
    marque: '',
    modele: '',
    annee: null,
    numero_serie: '',
    immatriculation: '',
    puissance_cv: null,
    heures_compteur: 0,
    photo_url: '',
    statut: 'operationnel'
  });
  
  // Charger les données
  const fetchMachines = async () => {
    setLoading(true);
    try {
      const response = await api.get('/materiel');
      setMachines(response.data.machines || []);
      setStats(response.data.stats || {});
    } catch (error) {
      console.error('Erreur chargement matériel:', error);
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchMachines();
  }, []);
  
  // Filtrer les machines
  const filteredMachines = machines.filter(m => {
    if (typeFilter !== 'all' && m.type_machine !== typeFilter) return false;
    if (statutFilter !== 'all' && m.statut !== statutFilter) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return m.nom?.toLowerCase().includes(query) ||
             m.marque?.toLowerCase().includes(query) ||
             m.modele?.toLowerCase().includes(query) ||
             m.immatriculation?.toLowerCase().includes(query);
    }
    return true;
  });

  // Grouper par type pour affichage classé (ex: tous les porte-bennes à la suite)
  // Inclut les types legacy non déclarés dans TYPES_MACHINES
  const TYPE_LEGACY_LABELS = {
    vehicule: { nom: 'Véhicule', icone: '🚗', couleur: '#f97316' },
    recolteuse: { nom: 'Récolteuse', icone: '🌾', couleur: '#eab308' },
    outil_sol: { nom: 'Outil du sol', icone: '⛏️', couleur: '#a16207' },
    entretien: { nom: 'Entretien', icone: '🔧', couleur: '#475569' },
    motoculteur: { nom: 'Motoculteur', icone: '⚙️', couleur: '#65a30d' },
    decortiqueuse: { nom: 'Décortiqueuse', icone: '🌰', couleur: '#92400e' },
    repiqueuse: { nom: 'Repiqueuse', icone: '🌱', couleur: '#15803d' },
    sechoir: { nom: 'Séchoir', icone: '🔥', couleur: '#dc2626' },
    cave: { nom: 'Cave', icone: '🍷', couleur: '#7c2d12' },
  };
  const groupedByType = (() => {
    // Récupérer tous les types présents
    const typeMap = {};
    filteredMachines.forEach((m) => {
      const t = m.type_machine || 'autre';
      (typeMap[t] = typeMap[t] || []).push(m);
    });
    // Ordonner : d'abord TYPE_ORDER, puis les types legacy alphabétiquement
    const orderedTypes = [];
    TYPE_ORDER.forEach((t) => { if (typeMap[t]) orderedTypes.push(t); });
    Object.keys(typeMap)
      .filter((t) => !TYPE_ORDER.includes(t))
      .sort((a, b) => a.localeCompare(b))
      .forEach((t) => orderedTypes.push(t));
    return orderedTypes.map((t) => ({
      type: t,
      info: TYPES_MACHINES[t] || TYPE_LEGACY_LABELS[t] || { nom: t.charAt(0).toUpperCase() + t.slice(1), icone: '🔧', couleur: '#9ca3af' },
      items: typeMap[t].sort((a, b) => (a.nom || '').localeCompare(b.nom || '')),
    }));
  })();
  
  // Créer une machine
  const handleCreateMachine = async () => {
    try {
      await api.post('/materiel', newMachine);
      toast.success('Machine ajoutée');
      setShowAddDialog(false);
      setNewMachine({
        nom: '', type_machine: 'tracteur', marque: '', modele: '',
        annee: null, numero_serie: '', immatriculation: '',
        puissance_cv: null, heures_compteur: 0, photo_url: '', statut: 'operationnel'
      });
      fetchMachines();
    } catch (error) {
      toast.error('Erreur création');
    }
  };

  if (loading) {
    return (
      <div className="h-[calc(100vh-200px)] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="materiel-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Tractor className="w-8 h-8" />
            Parc Matériel
          </h1>
          <p className="text-muted-foreground">
            Gestion des machines, entretiens et documentation
          </p>
        </div>
        
        <div className="flex gap-2">
          <Button onClick={() => setShowAddDialog(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Nouvelle machine
          </Button>
          <Button variant="outline" onClick={() => setShowDoublonsDialog(true)} data-testid="btn-doublons-materiel">
            <Copy className="w-4 h-4 mr-2" />
            Doublons
          </Button>
          <Button variant="outline" onClick={fetchMachines}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>
      
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
          <CardContent className="p-4">
            <Tractor className="w-6 h-6 mb-2 opacity-80" />
            <p className="text-3xl font-bold">{stats.total || machines.length}</p>
            <p className="text-xs text-green-100">Total machines</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <CardContent className="p-4">
            <Settings className="w-6 h-6 mb-2 opacity-80" />
            <p className="text-3xl font-bold">{stats.operationnels || 0}</p>
            <p className="text-xs text-blue-100">Opérationnels</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-red-500 to-red-600 text-white">
          <CardContent className="p-4">
            <AlertTriangle className="w-6 h-6 mb-2 opacity-80" />
            <p className="text-3xl font-bold">{stats.en_panne || 0}</p>
            <p className="text-xs text-red-100">En panne</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
          <CardContent className="p-4">
            <Wrench className="w-6 h-6 mb-2 opacity-80" />
            <p className="text-3xl font-bold">{stats.avec_alertes || 0}</p>
            <p className="text-xs text-amber-100">Alertes entretien</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
          <CardContent className="p-4">
            <DollarSign className="w-6 h-6 mb-2 opacity-80" />
            <p className="text-3xl font-bold">{((stats.cout_entretien_annee || 0) / 1000).toFixed(1)}k€</p>
            <p className="text-xs text-purple-100">Coût entretien/an</p>
          </CardContent>
        </Card>
      </div>
      
      {/* Filtres */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Rechercher une machine..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous types</SelectItem>
                {Object.entries(TYPES_MACHINES).map(([key, info]) => (
                  <SelectItem key={key} value={key}>
                    {info.icone} {info.nom}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={statutFilter} onValueChange={setStatutFilter}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous statuts</SelectItem>
                <SelectItem value="operationnel">✅ Opérationnel</SelectItem>
                <SelectItem value="en_panne">🔴 En panne</SelectItem>
                <SelectItem value="en_entretien">🟠 En entretien</SelectItem>
              </SelectContent>
            </Select>
            
            <Badge variant="secondary">{filteredMachines.length} machine(s)</Badge>
          </div>
        </CardContent>
      </Card>
      
      {/* Liste des machines groupées par type */}
      <div className="space-y-8" data-testid="materiel-grouped-list">
        {groupedByType.map(group => {
          const info = group.info;
          return (
            <div key={group.type} data-testid={`materiel-group-${group.type}`}>
              <div className="flex items-center gap-2 mb-3 pb-2 border-b">
                <span className="text-2xl">{info.icone}</span>
                <h2 className="text-lg font-semibold" style={{ color: info.couleur }}>
                  {info.nom}
                </h2>
                <Badge variant="outline">{group.items.length}</Badge>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {group.items.map(machine => (
                  <MachineCard
                    key={machine.id}
                    machine={machine}
                    onClick={setSelectedMachine}
                  />
                ))}
              </div>
            </div>
          );
        })}

        {filteredMachines.length === 0 && (
          <div className="text-center py-12 text-muted-foreground">
            <Tractor className="w-16 h-16 mx-auto mb-4 opacity-30" />
            <p className="text-lg">Aucune machine trouvée</p>
            <Button variant="link" onClick={() => setShowAddDialog(true)}>
              Ajouter une machine
            </Button>
          </div>
        )}
      </div>
      
      {/* Fiche machine détaillée */}
      {selectedMachine && (
        <FicheMachine
          machine={selectedMachine}
          onClose={() => setSelectedMachine(null)}
          onUpdate={fetchMachines}
          onDelete={fetchMachines}
        />
      )}
      
      {/* Dialog Ajout Machine */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Nouvelle machine</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nom *</Label>
                <Input
                  value={newMachine.nom}
                  onChange={(e) => setNewMachine({...newMachine, nom: e.target.value})}
                  placeholder="Ex: Tracteur principal"
                />
              </div>
              <div className="space-y-2">
                <Label>Type *</Label>
                <Select 
                  value={newMachine.type_machine}
                  onValueChange={(v) => setNewMachine({...newMachine, type_machine: v})}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(TYPES_MACHINES).map(([key, info]) => (
                      <SelectItem key={key} value={key}>
                        {info.icone} {info.nom}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Marque</Label>
                <Input
                  value={newMachine.marque}
                  onChange={(e) => setNewMachine({...newMachine, marque: e.target.value})}
                  placeholder="Ex: John Deere"
                />
              </div>
              <div className="space-y-2">
                <Label>Modèle</Label>
                <Input
                  value={newMachine.modele}
                  onChange={(e) => setNewMachine({...newMachine, modele: e.target.value})}
                  placeholder="Ex: 6120M"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Année</Label>
                <Input
                  type="number"
                  value={newMachine.annee || ''}
                  onChange={(e) => setNewMachine({...newMachine, annee: parseInt(e.target.value) || null})}
                  placeholder="2020"
                />
              </div>
              <div className="space-y-2">
                <Label>Puissance (cv)</Label>
                <Input
                  type="number"
                  value={newMachine.puissance_cv || ''}
                  onChange={(e) => setNewMachine({...newMachine, puissance_cv: parseInt(e.target.value) || null})}
                />
              </div>
              <div className="space-y-2">
                <Label>Heures compteur</Label>
                <Input
                  type="number"
                  value={newMachine.heures_compteur || ''}
                  onChange={(e) => setNewMachine({...newMachine, heures_compteur: parseFloat(e.target.value) || 0})}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Immatriculation</Label>
                <Input
                  value={newMachine.immatriculation}
                  onChange={(e) => setNewMachine({...newMachine, immatriculation: e.target.value})}
                  placeholder="AB-123-CD"
                />
              </div>
              <div className="space-y-2">
                <Label>N° de série</Label>
                <Input
                  value={newMachine.numero_serie}
                  onChange={(e) => setNewMachine({...newMachine, numero_serie: e.target.value})}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>URL Photo</Label>
              <Input
                value={newMachine.photo_url}
                onChange={(e) => setNewMachine({...newMachine, photo_url: e.target.value})}
                placeholder="https://..."
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>Annuler</Button>
            <Button onClick={handleCreateMachine} disabled={!newMachine.nom}>
              <Plus className="w-4 h-4 mr-2" />
              Créer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Doublons */}
      <Dialog open={showDoublonsDialog} onOpenChange={(open) => {
        setShowDoublonsDialog(open);
        if (open) {
          setLoadingDoublons(true);
          api.get('/materiel/admin/doublons')
            .then(r => {
              const grps = r.data.groupes || [];
              setDoublonsGroupes(grps);
              // Sélection par défaut : la machine la plus récente
              const ks = {};
              grps.forEach(g => {
                const sorted = [...g.machines].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
                ks[g.nom_normalise] = sorted[0]?.id;
              });
              setKeepIds(ks);
            })
            .catch(() => toast.error('Erreur chargement doublons'))
            .finally(() => setLoadingDoublons(false));
        }
      }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="dialog-doublons">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Copy className="w-5 h-5" />
              Détection de doublons
            </DialogTitle>
          </DialogHeader>
          {loadingDoublons ? (
            <div className="py-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
          ) : doublonsGroupes.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">
              <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-2" />
              <p>Aucun doublon détecté ! 🎉</p>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                {doublonsGroupes.length} groupe(s) de doublons détecté(s) — sélectionnez la machine à conserver, les autres seront supprimées (entretiens et alertes transférés).
              </p>
              {doublonsGroupes.map(g => (
                <Card key={g.nom_normalise} className="border-amber-200">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2 flex-wrap">
                      <span>{g.nom_affichage}</span>
                      <Badge variant="outline">{g.count} occurrences</Badge>
                      {(g.critere_match || []).map(c => (
                        <Badge key={c} variant="secondary" className="text-xs">
                          {c === 'nom' ? '📝 même nom' : c === 'immatriculation' ? '🚗 même immat' : c === 'numero_serie' ? '🔢 même n° série' : c}
                        </Badge>
                      ))}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {g.machines.map(m => (
                      <div
                        key={m.id}
                        className={`flex items-center justify-between p-2 border rounded cursor-pointer hover:bg-accent ${keepIds[g.nom_normalise] === m.id ? 'border-green-500 bg-green-50 dark:bg-green-950/20' : ''}`}
                        onClick={() => setKeepIds(prev => ({ ...prev, [g.nom_normalise]: m.id }))}
                      >
                        <div className="flex items-center gap-3">
                          <input
                            type="radio"
                            checked={keepIds[g.nom_normalise] === m.id}
                            readOnly
                            className="w-4 h-4"
                          />
                          <div>
                            <p className="font-medium text-sm">{m.nom}</p>
                            <p className="text-xs text-muted-foreground">
                              {m.marque} {m.modele}
                              {m.immatriculation && ` · 🚗 ${m.immatriculation}`}
                              {m.numero_serie && ` · 🔢 ${m.numero_serie}`}
                              {m.type_machine && ` · ${m.type_machine}`}
                              {m.heures_compteur != null && ` · ${m.heures_compteur}h`}
                              {m.photos_count > 0 && ` · ${m.photos_count} photos`}
                            </p>
                            <p className="text-[10px] text-muted-foreground">
                              {m.created_at ? new Date(m.created_at).toLocaleDateString('fr-FR') : '?'}
                            </p>
                          </div>
                        </div>
                        {keepIds[g.nom_normalise] === m.id && (
                          <Badge className="bg-green-500">À conserver</Badge>
                        )}
                      </div>
                    ))}
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={async () => {
                        const keep_id = keepIds[g.nom_normalise];
                        const delete_ids = g.machines.map(m => m.id).filter(id => id !== keep_id);
                        if (delete_ids.length === 0) return;
                        if (!window.confirm(`Fusionner ${delete_ids.length} doublon(s) dans la machine sélectionnée ?`)) return;
                        try {
                          const r = await api.post('/materiel/admin/fusion-doublons', { keep_id, delete_ids });
                          toast.success(`${r.data.merged} doublon(s) fusionné(s)`);
                          setDoublonsGroupes(prev => prev.filter(x => x.nom_normalise !== g.nom_normalise));
                          fetchMachines();
                        } catch (e) {
                          toast.error('Erreur fusion: ' + (e.response?.data?.detail || e.message));
                        }
                      }}
                      data-testid={`btn-fusionner-${g.nom_normalise}`}
                    >
                      Fusionner ce groupe ({g.count - 1} suppression(s))
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDoublonsDialog(false)}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
